import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MapPin } from "lucide-react";

/**
 * Inline chip strip of the dispatcher's most-used pickup or drop-off
 * addresses. Renders above the PlacesInput when the input is empty so the
 * dispatcher can fill a known location with one click instead of typing.
 *
 * Once they start typing (3+ chars) we hide the chips and let Google Places
 * Autocomplete handle the rest.
 */
export default function AddressFavorites({ kind, currentValue, onPick }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Load the top-N addresses for this dispatcher once on mount.
  useEffect(() => {
    api.get(`/admin/addresses/search`, { params: { q: "  ", kind, limit: 8 } })
      .then(() => {})
      .catch(() => {});
    // The endpoint requires q>=2 chars. We grab the most frequent matches for
    // a curated set of common Wisconsin keywords so the strip is populated
    // even before the dispatcher types anything.
    const probes = ["mke", "milwaukee", "airport", "ave", "st", "wi"];
    Promise.all(probes.map(p =>
      api.get(`/admin/addresses/search`, { params: { q: p, kind, limit: 6 } })
        .then(r => r.data).catch(() => [])
    )).then((all) => {
      const seen = {};
      all.flat().forEach(it => {
        const k = (it.address || "").trim();
        if (!k) return;
        seen[k] = Math.max(seen[k] || 0, it.count || 0);
      });
      const ranked = Object.entries(seen)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([address, count]) => ({ address, count }));
      setItems(ranked);
      setLoaded(true);
    });
  }, [kind]);

  // Hide once the dispatcher starts typing — Google Places takes over.
  if (!loaded || items.length === 0) return null;
  if ((currentValue || "").trim().length >= 3) return null;

  return (
    <div
      data-testid={`addr-favs-${kind}`}
      className="flex flex-wrap gap-1.5 mb-2"
    >
      {items.map(it => (
        <button
          key={it.address}
          type="button"
          onClick={() => onPick(it.address)}
          title={it.address}
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] px-2 py-1 border border-[#D4AF37]/30 text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-colors max-w-[260px]"
        >
          <MapPin size={10} className="shrink-0 text-[#D4AF37]/70"/>
          <span className="truncate">{it.address}</span>
          <span className="text-[#71717A]/70 text-[9px]">·{it.count}</span>
        </button>
      ))}
    </div>
  );
}
